import { createApi } from 'unsplash-js';

const unsplash = createApi({
  accessKey: 'JMoh_nas4TbcmE7XSni_EieBrMNXVLoY_z7XE0g5RHk'
});

import { doc, getDoc, setDoc, collection } from 'firebase/firestore';
import { db } from './firebase';

interface CachedPhoto {
  url: string;
  createdAt: Date;
  expiresAt: Date;
}

const CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 jours

async function getCachedPhotos(city: string): Promise<string[] | null> {
  try {
    const cacheRef = doc(db, 'photo_cache', city.toLowerCase());
    const cacheDoc = await getDoc(cacheRef);
    
    if (cacheDoc.exists()) {
      const data = cacheDoc.data();
      const photos = data.photos as CachedPhoto[];
      // Vérifier si le cache est encore valide
      if (photos[0].expiresAt.toDate().getTime() > Date.now()) {
        return photos.map(photo => photo.url);
      }
    }
    return null;
  } catch (error) {
    console.error('Error checking photo cache:', error);
    return null;
  }
}

async function cachePhotos(city: string, photos: string[]) {
  try {
    const now = new Date();
    const expires = new Date(now.getTime() + CACHE_DURATION);
    
    // Store as an object with photos array instead of array directly
    const photoData = {
      photos: photos.map(url => ({
        url,
        createdAt: now,
        expiresAt: expires
      })),
      lastUpdated: now
    };
    
    await setDoc(doc(db, 'photo_cache', city.toLowerCase()), photoData);
  } catch (error) {
    console.error('Error caching photos:', error);
  }
}

export async function getCityPhotos(city: string, count: number = 1): Promise<string[]> {
  try {
    // Vérifier d'abord le cache
    const cachedPhotos = await getCachedPhotos(city);
    if (cachedPhotos) {
      return cachedPhotos;
    }
    
    // Si pas de cache, créer une nouvelle entrée
    const result = await unsplash.search.getPhotos({
      query: `${city}`,
      page: 1,
      perPage: 10, // Get more photos to have variety
      orientation: 'landscape',
      contentFilter: 'high',
      orderBy: 'relevant'
    });

    if (result.errors) {
      console.error('Error fetching Unsplash photos:', result.errors[0]);
      return [];
    }

    const photos = result.response?.results.map(photo => 
      photo.urls.regular + '&q=90&w=1200&h=800&fit=crop&crop=edges'
    ) || [];
    
    if (photos.length > 0) {
      try {
        await cachePhotos(city, photos);
      } catch (error) {
        // Si l'utilisateur n'a pas les droits, on continue sans mettre en cache
        console.warn('Could not cache photos:', error);
      }
    }
    
    return photos;
  } catch (error) {
    console.error('Error fetching Unsplash photos:', error);
    return [];
  }
}

export async function getRandomCityPhoto(city: string): Promise<string | null> {
  const photos = await getCityPhotos(city);
  if (photos.length === 0) {
    // Vérifier le cache pour les photos de fallback
    const cachedFallback = await getCachedPhotos(`${city}_fallback`);
    if (cachedFallback) {
      return cachedFallback[Math.floor(Math.random() * cachedFallback.length)] || null;
    }
    
    // Fallback to a more generic search if no specific photos found
    const fallbackPhotos = await unsplash.search.getPhotos({
      query: `${city} city view`,
      page: 1,
      perPage: 5,
      orientation: 'landscape',
      contentFilter: 'high'
    });
    
    if (fallbackPhotos.errors || !fallbackPhotos.response) return null;
    
    const fallbackUrls = fallbackPhotos.response.results.map(photo =>
      photo.urls.regular + '&q=90&w=1200&h=800&fit=crop&crop=edges'
    );
    
    // Mettre en cache les photos de fallback
    if (fallbackUrls.length > 0) {
      await cachePhotos(`${city}_fallback`, fallbackUrls);
    }
    
    return fallbackUrls[Math.floor(Math.random() * fallbackUrls.length)] || null;
  }
  
  return photos[Math.floor(Math.random() * photos.length)] || null;
}